import { useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronRight, ExternalLink } from "lucide-react";
import type {
  CashFlowRow,
  PeriodMeta,
} from "@/hooks/finance/cash-flow/useCashFlow";
import { RowSparkline } from "./statement-table.utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

type Props = {
  row: CashFlowRow;
  periodLabels: { key: string; label: string }[];
  periodKeys: string[];
  formatValue: (val: number) => string;
  openingMetaByPeriod?: Map<string, PeriodMeta>;
  closingMetaByPeriod?: Map<string, PeriodMeta>;
  dateFrom?: string;
  dateTo?: string;
};

function formatMissingAmounts(amounts?: Record<string, number>) {
  if (!amounts) return [];

  return Object.entries(amounts)
    .filter(([, value]) => Number(value) > 0)
    .map(([currency, value]) => `${currency}: ${value.toLocaleString()}`);
}

function getMetaForRow(
  rowKey: string,
  periodKey: string,
  openingMetaByPeriod?: Map<string, PeriodMeta>,
  closingMetaByPeriod?: Map<string, PeriodMeta>
) {
  if (rowKey === "opening") return openingMetaByPeriod?.get(periodKey);
  if (rowKey === "closing") return closingMetaByPeriod?.get(periodKey);
  return undefined;
}

function formatMoney(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRate(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

function AccountList({
  title,
  items,
  emptyText,
  showConversion = false,
}: {
  title: string;
  items: Array<{
    accountId: string;
    accountName: string;
    currency: string | null;
    amount: number | null;
    rateType?: string | null;
    appliedRate?: number | null;
    convertedAmount?: number | null;
    targetCurrency?: string | null;
    rateDate?: string | null;
  }>;
  emptyText: string;
  showConversion?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-slate-900">{title}</div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
          {emptyText}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={`${title}-${item.accountId}`}
              className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {item.accountName}
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                    <span>
                      Balance:{" "}
                      <span className="font-medium text-slate-900">
                        {formatMoney(item.amount)} {item.currency ?? ""}
                      </span>
                    </span>

                    {showConversion && (
                      <>
                        <span>
                          Rate:{" "}
                          <span className="font-medium text-slate-900">
                            {formatRate(item.appliedRate)}
                          </span>
                        </span>

                        <span>
                          Type:{" "}
                          <span className="font-medium text-slate-900">
                            {item.rateType ?? "—"}
                          </span>
                        </span>

                        <span>
                          FX date:{" "}
                          <span className="font-medium text-slate-900">
                            {item.rateDate ?? "—"}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {showConversion ? (
                  <div className="shrink-0 text-right">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                      Converted
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {formatMoney(item.convertedAmount)} {item.targetCurrency ?? ""}
                    </div>
                  </div>
                ) : (
                  <div className="shrink-0 text-sm font-medium text-slate-700">
                    {item.amount == null
                      ? "No balance"
                      : `${formatMoney(item.amount)} ${item.currency ?? ""}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PeriodMetaDialogBody({
  meta,
  rowLabel,
  periodLabel,
}: {
  meta: PeriodMeta;
  rowLabel: string;
  periodLabel: string;
}) {
  const missingFxAmounts = formatMissingAmounts(meta.missingFxAmountByCurrency);
  const missingSourceAmounts = formatMissingAmounts(
    meta.missingSourceAmountByCurrency
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="text-sm text-slate-600">
          <span className="font-medium text-slate-900">{rowLabel}</span>
          {" • "}
          <span>{periodLabel}</span>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This balance is partial. Some accounts were included in the
          calculation, but others could not be used because source balance data
          or FX conversion data is missing.
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">
            Included accounts
          </div>
          <div className="mt-1 text-2xl font-semibold text-emerald-700">
            {meta.includedAccounts.length}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">
            Missing source
          </div>
          <div className="mt-1 text-2xl font-semibold text-amber-700">
            {meta.missingSourceAccountsCount}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">
            Missing FX
          </div>
          <div className="mt-1 text-2xl font-semibold text-rose-700">
            {meta.missingFxAccountsCount}
          </div>
        </div>
      </div>

      {missingSourceAmounts.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-sm font-semibold text-amber-900">
            Missing source amounts
          </div>
          <div className="mt-2 text-sm text-amber-800">
            {missingSourceAmounts.join(", ")}
          </div>
        </div>
      )}

      {missingFxAmounts.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <div className="text-sm font-semibold text-rose-900">
            Missing FX amounts
          </div>
          <div className="mt-2 text-sm text-rose-800">
            {missingFxAmounts.join(", ")}
          </div>
        </div>
      )}

      <AccountList
  title="Included accounts"
  items={meta.includedAccounts}
  emptyText="No accounts were included in this balance."
  showConversion
/>

      <AccountList
  title="Accounts missing source balance data"
  items={meta.missingSourceAccounts}
  emptyText="No accounts are missing source balance data."
/>

      <AccountList
  title="Accounts missing FX conversion"
  items={meta.missingFxAccounts}
  emptyText="No accounts are missing FX conversion."
/>
    </div>
  );
}

function DataRow({
  row,
  periodLabels,
  periodKeys,
  formatValue,
  openingMetaByPeriod,
  closingMetaByPeriod,
  dateFrom,
  dateTo,
  isChild = false,
}: Props & { isChild?: boolean }) {
  const isTotal = row.isTotal;
  const isGrandTotal = row.key === "net-cash-flow" || row.key === "closing";
  const expenseHref = row.categoryId
    ? `/finance/accounting/expenses?cat=${encodeURIComponent(row.categoryId)}${dateFrom ? `&from=${dateFrom}` : ""}${dateTo ? `&to=${dateTo}` : ""}`
    : null;

  return (
    <tr
      className={cn(
        "group transition-colors",
        isChild && "bg-slate-50/30",
        isTotal
          ? isGrandTotal
            ? "bg-slate-900 text-white hover:bg-slate-800"
            : "bg-slate-50 hover:bg-slate-100"
          : "hover:bg-slate-50/50"
      )}
    >
      <td
        className={cn(
          "sticky left-0 z-10 px-4 py-2 border-r transition-colors shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]",
          isGrandTotal
            ? "bg-slate-900 border-slate-700"
            : isChild
              ? "bg-slate-50/30 group-hover:bg-slate-100/50 border-slate-100"
              : "bg-white group-hover:bg-slate-50 border-slate-100"
        )}
      >
        <div
          className={cn(
            "text-[11px] truncate flex items-center justify-between",
            isTotal ? "font-bold text-slate-900" : "text-slate-600 font-medium",
            isGrandTotal && "text-white",
            isChild && "text-slate-500 text-[10px]"
          )}
          style={{
            paddingLeft: row.indent ? `${row.indent * 12}px` : undefined,
          }}
        >
          {expenseHref ? (
            <a
              href={expenseHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-primary hover:underline transition-colors"
              title="Open expenses for this cost position in a new tab"
            >
              {row.label}
              <ExternalLink className="h-2.5 w-2.5 opacity-60" />
            </a>
          ) : (
            row.label
          )}

          {!isTotal && !isChild && (
            <div className="h-4 w-12 opacity-0 group-hover:opacity-100 transition-opacity">
              <RowSparkline
                data={periodKeys.map((k) => row.values.get(k) || 0)}
                color={row.key.includes("sales") ? "#10B981" : "#94A3B8"}
              />
            </div>
          )}
        </div>
      </td>

      {periodLabels.map((p) => {
        const val = row.values.get(p.key) || 0;
        const meta = getMetaForRow(
          row.key,
          p.key,
          openingMetaByPeriod,
          closingMetaByPeriod
        );

        const isPartial = !!meta?.isPartial;

        const cellContent = (
          <div className="inline-flex items-center justify-end gap-2 w-full">
            {isPartial && (
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                  isGrandTotal
                    ? "bg-amber-400/20 text-amber-200"
                    : "bg-amber-100 text-amber-700"
                )}
              >
                Partial
              </span>
            )}
            <span>{formatValue(val)}</span>
          </div>
        );

        return (
          <td
            key={p.key}
            className={cn(
              "px-4 py-2 text-right tabular-nums text-[11px] font-medium",
              isTotal && "font-black",
              isGrandTotal && "text-white py-3",
              isChild && "text-[10px] font-normal",
              !isTotal && val < 0 && "text-rose-500",
              !isTotal && val > 0 && "text-emerald-600",
              val === 0 && !isTotal && "text-slate-300 font-normal"
            )}
          >
            {isPartial ? (
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="w-full cursor-pointer text-right"
                  >
                    {cellContent}
                  </button>
                </DialogTrigger>

                <DialogContent className="max-w-3xl bg-white">
                  <DialogHeader>
                    <DialogTitle>{row.label} breakdown</DialogTitle>
                  </DialogHeader>

                  <div className="max-h-[70vh] overflow-y-auto pr-2">
                    <PeriodMetaDialogBody
                      meta={meta!}
                      rowLabel={row.label}
                      periodLabel={p.label}
                    />
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              cellContent
            )}
          </td>
        );
      })}
    </tr>
  );
}

export function StatementTableRow({
  row,
  periodLabels,
  periodKeys,
  formatValue,
  openingMetaByPeriod,
  closingMetaByPeriod,
  dateFrom,
  dateTo,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = row.children && row.children.length > 0;
  const isHeader = row.isHeader;
  const isTotal = row.isTotal;
  const isGrandTotal = row.key === "net-cash-flow" || row.key === "closing";

  if (isHeader) {
    return (
      <tr className="bg-slate-50/80 border-t-2 border-b border-slate-200/60">
        <td className="sticky left-0 z-10 bg-inherit px-4 py-2 border-r border-slate-200/60">
          <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">
            {row.label}
          </span>
        </td>

        {periodLabels.map((p) => (
          <td key={p.key} className="py-2 px-4" />
        ))}
      </tr>
    );
  }

  // For rows with children, render a clickable parent + conditionally expanded children
  if (hasChildren) {
    return (
      <>
        <tr
          className={cn(
            "group transition-colors cursor-pointer",
            isTotal
              ? isGrandTotal
                ? "bg-slate-900 text-white hover:bg-slate-800"
                : "bg-slate-50 hover:bg-slate-100"
              : "hover:bg-slate-50/50"
          )}
          onClick={() => setExpanded(!expanded)}
        >
          <td
            className={cn(
              "sticky left-0 z-10 px-4 py-2 border-r transition-colors shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]",
              isGrandTotal
                ? "bg-slate-900 border-slate-700"
                : "bg-white group-hover:bg-slate-50 border-slate-100"
            )}
          >
            <div
              className={cn(
                "text-[11px] truncate flex items-center gap-1.5",
                isTotal ? "font-bold text-slate-900" : "text-slate-600 font-medium",
                isGrandTotal && "text-white"
              )}
              style={{
                paddingLeft: row.indent ? `${row.indent * 12}px` : undefined,
              }}
            >
              <ChevronRight
                className={cn(
                  "h-3 w-3 shrink-0 transition-transform duration-200 text-slate-400",
                  expanded && "rotate-90"
                )}
              />
              {row.label}

              <span className="text-[9px] text-slate-400 font-normal ml-1">
                {row.children!.length}
              </span>

              <div className="flex-1" />
              <div className="h-4 w-12 opacity-0 group-hover:opacity-100 transition-opacity">
                <RowSparkline
                  data={periodKeys.map((k) => row.values.get(k) || 0)}
                  color="#94A3B8"
                />
              </div>
            </div>
          </td>

          {periodLabels.map((p) => {
            const val = row.values.get(p.key) || 0;
            return (
              <td
                key={p.key}
                className={cn(
                  "px-4 py-2 text-right tabular-nums text-[11px] font-medium",
                  isTotal && "font-black",
                  isGrandTotal && "text-white py-3",
                  !isTotal && val < 0 && "text-rose-500",
                  !isTotal && val > 0 && "text-emerald-600",
                  val === 0 && !isTotal && "text-slate-300 font-normal"
                )}
              >
                {formatValue(val)}
              </td>
            );
          })}
        </tr>

        {expanded &&
          row.children!.map((child) => (
            <DataRow
              key={child.key}
              row={child}
              periodLabels={periodLabels}
              periodKeys={periodKeys}
              formatValue={formatValue}
              openingMetaByPeriod={openingMetaByPeriod}
              closingMetaByPeriod={closingMetaByPeriod}
              dateFrom={dateFrom}
              dateTo={dateTo}
              isChild
            />
          ))}
      </>
    );
  }

  return (
    <DataRow
      row={row}
      periodLabels={periodLabels}
      periodKeys={periodKeys}
      formatValue={formatValue}
      openingMetaByPeriod={openingMetaByPeriod}
      closingMetaByPeriod={closingMetaByPeriod}
      dateFrom={dateFrom}
      dateTo={dateTo}
    />
  );
}